import { Col, FormCheck, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import InputMoney from "../../../components/Forms/Element/InputMoney";
import InputPercent from "../../../components/Forms/Element/InputPercent";
import ModalPage from "../../../components/Common/Modal/ModalPage";
import InputText from "../../../components/Forms/Element/InputText";
import PeriodicitySelect from "../../../components/Custom/Subscription/Plan/PeriodicitySelect";
import HintModalTrigger from "../../../components/Common/HintModalTrigger";
import StatusCheckBox from "../../../components/Common/StatusCheckbox";
import { convertStatusToBoolean, convertDoubleToStringPTBR } from "../../../../utils/ConverterUtils";
import HintTitle from "../../../components/Common/HintTitle";
import FieldValidationError from "../../../components/Forms/Element/FieldValidationError";
import PlanCycleSummary from "../../../components/Custom/Subscription/Plan/PlanCycleSummary";
import InputNumeric from "../../../components/Forms/Element/InputNumeric";
import ProductMultiSelect from "../../../components/Custom/Main/Product/ProductMultiSelect";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import PaymentProvider from "../../../../constants/PaymentProvider";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import { resolveSingleParameter } from "../../../../utils/ParameterUtils";
import FormSection from "../../../components/Common/Form/FormSection";
import HintTooltip from "../../../components/Common/HintTooltip";
import HintCallout from "../../../components/Common/HintCallout";
import { ALL_WORK_DAYS, WEEK_DAYS } from "../../../../utils/WorkDayUtils";
import PlanContractSection from "../../../components/Custom/Subscription/Plan/PlanContractSection";
import "./SubscriptionPlanForm.css";

const Form = (props) => {

    const { t } = useTranslation();
    const { formik, status, setStatus, entityData, currentProvider } = props;
    const [showQtyTransactionsHint, setShowQtyTransactionsHint] = useState(false);
    const [showMaxAppointmentsHint, setShowMaxAppointmentsHint] = useState(false);
    const [allProducts, setAllProducts] = useState([]);
    // Desconto usado ao adicionar por categoria. Começa em 100, o mesmo padrão de quem escolhe
    // o item direto na lista: o caso mais comum é incluir a categoria inteira no plano.
    const [categoryFillPct, setCategoryFillPct] = useState('100');
    // Última categoria adicionada, para a tela confirmar o que entrou no plano.
    const [categoryFillResult, setCategoryFillResult] = useState(null);
    // Último número de ciclos informado, pra restaurar se o usuário desmarcar "Indeterminado".
    const [lastQtyTransactions, setLastQtyTransactions] = useState(12);
    const isIndeterminateQty = Number(formik.values.qtyTransactions) === 0;
    // Com assinatura aberta a duração em dias não muda: alteraria a validade de quem já pagou.
    const packageDaysLocked = Number(entityData?.openSubscriptionCount) > 0;
    // Limite geral de agendamentos futuros (Configurações > Agendamento). Só serve para a tela
    // dizer quanto vale o campo vazio; nulo enquanto não carregou ou se a leitura falhar.
    const [generalFutureLimit, setGeneralFutureLimit] = useState(null);
    // Dias gerais do calendário do link, pelo mesmo motivo: dizer quanto vale o campo vazio.
    const [generalBookingDays, setGeneralBookingDays] = useState(null);

    /** Marca ou desmarca um dia de uso do plano, preservando os demais. */
    const toggleBenefitDay = (day) => {
        const current = formik.values.benefitDays || [];
        const updated = current.includes(day)
            ? current.filter(item => item !== day)
            : [...current, day];

        formik.setFieldValue('benefitDays', updated);
    };

    /** Se o dia está marcado, para decidir o destaque visual do botão. */
    const isBenefitDaySelected = (day) => (formik.values.benefitDays || []).includes(day);

    // Semana inteira marcada = plano sem restrição de dia, e aí não existe "demais dias":
    // o campo de desconto fica desabilitado em vez de aceitar um valor que nunca seria usado.
    const allBenefitDaysSelected = (formik.values.benefitDays || []).length === ALL_WORK_DAYS.length;
    const noBenefitDaySelected = (formik.values.benefitDays || []).length === 0;

    /** Atalho do cabeçalho: marca a semana toda ou, se ela já está marcada, limpa tudo. */
    const toggleAllBenefitDays = () => {
        formik.setFieldValue('benefitDays', allBenefitDaysSelected ? [] : ALL_WORK_DAYS);
    };

    const offDayDiscount = Number(String(formik.values.offDayDiscountPercentage || '').replace(',', '.'));

    // Fala em "limite de usos", que é o nome do campo na tela, e não em "cota": o operador
    // precisa ligar a frase ao campo que ele mesmo preencheu acima.
    /** Resume em uma linha o que o plano faz nos dias marcados e nos demais. */
    const benefitDaysSummary = noBenefitDaySelected
        ? 'Marque os dias em que o benefício do plano vale.'
        : allBenefitDaysSelected
            ? 'O plano vale a semana toda: o benefício sempre se aplica e conta no limite de usos.'
            : offDayDiscount > 0
                ? `Nos dias marcados o benefício é integral e conta no limite de usos. Nos demais, o assinante paga ${convertDoubleToStringPTBR(offDayDiscount)}% menos e não gasta uso.`
                : 'Nos dias marcados o benefício é integral e conta no limite de usos. Nos demais, o assinante paga o valor cheio e não gasta uso.';

    const planFutureLimit = Number(formik.values.maxFutureAppointments) || 0;

    /** Diz em uma frase quantos horários o assinante deste plano pode deixar marcados. */
    const futureAppointmentsSummary = planFutureLimit > 0
        ? `O assinante deste plano pode deixar até ${planFutureLimit} ${planFutureLimit === 1 ? 'horário marcado' : 'horários marcados'} ao mesmo tempo pelo agendamento online. Para marcar outro, um deles precisa acontecer ou ser cancelado.`
        : generalFutureLimit
            ? `Vazio: segue a configuração geral de Agendamento, que hoje permite ${generalFutureLimit} ${generalFutureLimit === 1 ? 'horário marcado' : 'horários marcados'} por cliente ao mesmo tempo.`
            : 'Vazio: segue a configuração geral de Agendamento.';

    const planBookingDays = Number(formik.values.publicBookingDays) || 0;

    /** Diz em uma frase até quando o assinante deste plano enxerga a agenda pelo link. */
    const bookingDaysSummary = planBookingDays > 0
        ? `O calendário do link mostra ${planBookingDays} ${planBookingDays === 1 ? 'dia' : 'dias'} para este plano, contando hoje.`
        : generalBookingDays
            ? `Dias vazio: segue a configuração geral, que hoje mostra ${generalBookingDays} ${generalBookingDays === 1 ? 'dia' : 'dias'}.`
            : 'Dias vazio: segue a configuração geral.';

    function openQtyTransactionsHint() {
        setShowQtyTransactionsHint(true);
    }

    function openMaxAppointmentsHint() {
        setShowMaxAppointmentsHint(true);
    }

    useEffect(() => {
        axiosInstance.get("/main/products").then((res) => setAllProducts(res.data));
    }, []);

    useEffect(() => {
        // Mesma resolução da tela de configurações, para mostrar o mesmo número que ela.
        // Falhar aqui não impede salvar: a tela só deixa de citar o valor do geral.
        axiosInstance.get(Api.parameter.appointment)
            .then((resp) => {
                const data = resolveSingleParameter(resp.data);
                if (data && data.maxFutureAppointmentsPerCustomer) {
                    setGeneralFutureLimit(Math.max(1, data.maxFutureAppointmentsPerCustomer));
                }
                if (data && data.publicBookingDays) {
                    setGeneralBookingDays(data.publicBookingDays);
                }
            })
            .catch(() => {
                setGeneralFutureLimit(null);
                setGeneralBookingDays(null);
            });
    }, []);

    const categoryFillDiscount = parseFloat(categoryFillPct);
    const hasCategoryFillDiscount = !isNaN(categoryFillDiscount) && categoryFillDiscount >= 0 && categoryFillDiscount <= 100;

    /**
     * Categorias que têm algum item, com o total e quantos já estão no plano.
     *
     * Sai da mesma lista de produtos que o atalho usa para adicionar, e não do cadastro de
     * categorias: categoria sem item nenhum viraria uma etiqueta que não faz nada ao clicar.
     */
    const categoryOptions = useMemo(() => {
        const includedIds = new Set((formik.values.planProducts || []).map(pp => pp.productId));
        const byCategory = new Map();

        allProducts.forEach((product) => {
            const category = product.category;
            if (!category?.id) {
                return;
            }

            const entry = byCategory.get(category.id) || { id: category.id, name: category.name, total: 0, included: 0 };
            entry.total += 1;
            if (includedIds.has(product.id)) {
                entry.included += 1;
            }
            byCategory.set(category.id, entry);
        });

        return [...byCategory.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [allProducts, formik.values.planProducts]);

    /**
     * Adiciona ao plano os itens da categoria que ainda não estão nele, com o desconto informado.
     *
     * Itens que já estão no plano mantêm o desconto atual. É o que permite o fluxo comum de
     * incluir o serviço principal com 100% e depois as categorias com um desconto menor, sem
     * que a categoria sobrescreva o serviço principal.
     */
    function applyDiscountByCategory(category) {
        if (!category || !hasCategoryFillDiscount) return;

        const pct = categoryFillDiscount;
        const matched = allProducts.filter(
            (p) => p.category?.id === category.id
        );

        const currentProducts = formik.values.products || [];
        const currentPlanProducts = formik.values.planProducts || [];

        // Adiciona apenas os que ainda não estão na seleção
        const newProducts = matched
            .filter((p) => !currentProducts.find((fp) => fp.value === p.id))
            .map((p) => ({ value: p.id, label: p.name }));

        const newPlanProducts = matched
            .filter((p) => !currentPlanProducts.find((pp) => pp.productId === p.id))
            .map((p) => ({ productId: p.id, productName: p.name, discountPercentage: pct }));

        formik.setFieldValue('products', [...currentProducts, ...newProducts]);
        formik.setFieldValue('planProducts', [...currentPlanProducts, ...newPlanProducts]);

        // O desconto fica preenchido de propósito: o próximo clique costuma ser outra categoria
        // com o mesmo percentual.
        setCategoryFillResult({ name: category.name, added: newPlanProducts.length, pct });
    }

    useEffect(() => {
        if (entityData != null) {

            formik.setFieldValue('name', entityData.name);
            // InputMoney usa o valor numérico normalizado (ponto decimal) e sempre mostra centavos.
            formik.setFieldValue('price', String(entityData.price ?? entityData.creditCardPrice ?? ''));
            // null vindo da API significa "indeterminado"; convenção do form usa 0 pra isso (ver isIndeterminateQty).
            formik.setFieldValue('qtyTransactions', entityData.qtyTransactions ?? 0);
            formik.setFieldValue('dailyPackage', Boolean(entityData.packageDays));
            formik.setFieldValue('packageDays', entityData.packageDays ? String(entityData.packageDays) : '');
            setStatus(convertStatusToBoolean(entityData.status));

            formik.setFieldValue('maxAppointments', entityData.maxAppointments || '');
            formik.setFieldValue('maxFutureAppointments', entityData.maxFutureAppointments || '');
            formik.setFieldValue('publicBookingDays', entityData.publicBookingDays || '');
            // Lista vazia na API é "sem restrição de dia", que o form mostra com a semana toda marcada.
            formik.setFieldValue('benefitDays',
                entityData.benefitDays && entityData.benefitDays.length > 0 ? entityData.benefitDays : ALL_WORK_DAYS);
            // O InputPercent trabalha com a string numérica crua ("30.5") e formata sozinho para
            // "30,50"; mandar o texto já formatado em pt-BR faria ele ler a vírgula errado.
            formik.setFieldValue('offDayDiscountPercentage',
                entityData.offDayDiscountPercentage ? String(entityData.offDayDiscountPercentage) : '');

            // Preenche o select de produtos e os descontos de cada um
            if (entityData.planProducts !== null && entityData.planProducts.length > 0) {
                let products = [];
                let planProducts = [];
                entityData.planProducts.forEach(pp => {
                    products.push({ value: pp.productId, label: pp.productName });
                    planProducts.push({ productId: pp.productId, productName: pp.productName, discountPercentage: pp.discountPercentage });
                });
                formik.setFieldValue('products', products);
                formik.setFieldValue('planProducts', planProducts);
            }

            // Preenche o select de periodicidade
            if (entityData.periodicity !== null) {
                formik.setFieldValue('periodicity', {
                    value: entityData.periodicity,
                    label: t(entityData.periodicity.toLowerCase())
                });
            }
        }

        // eslint-disable-next-line
    }, [entityData]);

    return (
        <>
            <ModalPage
                title={<HintTitle />}
                show={showQtyTransactionsHint}
                setShow={setShowQtyTransactionsHint}
                child={
                    <>
                        <p className="text-muted mb-4">
                            Define quantas cobranças serão realizadas antes do plano encerrar automaticamente.
                        </p>

                        <div className="mb-4">
                            <HintCallout>
                                Marque a opção <strong>Indeterminado</strong> ao lado do campo para cobranças <strong>indefinidas</strong>. O plano continua cobrando até ser cancelado. É o padrão recomendado pra maioria dos planos.
                            </HintCallout>
                        </div>

                        <h6 className="text-uppercase fw-semibold mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em', color: '#94a3b8' }}>
                            Ou, se quiser um número fixo de cobranças
                        </h6>

                        <div className="d-flex flex-column gap-0" style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: '1rem' }}>
                            <div className="pb-3">
                                <div className="fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Mensal · 12 ciclos</div>
                                <p className="mb-0 text-muted" style={{ fontSize: '0.82rem' }}>1 cobrança/mês durante <strong>12 meses</strong>, depois encerra.</p>
                            </div>
                            <div className="pb-3">
                                <div className="fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Semestral · 4 ciclos</div>
                                <p className="mb-0 text-muted" style={{ fontSize: '0.82rem' }}>1 cobrança a cada 6 meses durante <strong>2 anos</strong> (4 cobranças).</p>
                            </div>
                            <div>
                                <div className="fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Anual · 1 ciclo</div>
                                <p className="mb-0 text-muted" style={{ fontSize: '0.82rem' }}>Uma única cobrança anual, depois encerra.</p>
                            </div>
                        </div>
                    </>
                }
                size="md"
            />

            <ModalPage
                title={<HintTitle />}
                show={showMaxAppointmentsHint}
                setShow={setShowMaxAppointmentsHint}
                child={
                    <>
                        <p className="text-muted mb-4">
                            Define quantas vezes o cliente pode utilizar os benefícios do plano dentro do seu ciclo de cobrança.
                        </p>

                        <h6 className="text-uppercase fw-semibold mb-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em', color: '#94a3b8' }}>
                            Exemplos
                        </h6>

                        <div className="d-flex flex-column gap-0 mb-4" style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: '1rem' }}>
                            {/* Cada serviço do plano gasta um uso, e o ciclo segue a periodicidade
                                a partir da contratação (PlanBenefitUsageService): os exemplos
                                deixam explícito o que mais confunde quem configura. */}
                            <div className="pb-3">
                                <div className="fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Mensal · Limite 4</div>
                                <p className="mb-0 text-muted" style={{ fontSize: '0.82rem' }}>Até <strong>4 serviços do plano por mês</strong> com o desconto, contando a partir da data de contratação. Corte e barba no mesmo atendimento contam como 2.</p>
                            </div>
                            <div className="pb-3">
                                <div className="fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Trimestral · Limite 12</div>
                                <p className="mb-0 text-muted" style={{ fontSize: '0.82rem' }}>Até <strong>12 nos três meses</strong> do ciclo, não 12 por mês.</p>
                            </div>
                            <div>
                                <div className="fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Ilimitado (vazio)</div>
                                <p className="mb-0 text-muted" style={{ fontSize: '0.82rem' }}>O desconto do plano vale em todos os atendimentos enquanto a assinatura estiver ativa.</p>
                            </div>
                        </div>

                        {/* O limite tira o desconto, não o horário: com a cota usada o cliente
                            continua agendando, inclusive pelo link, e paga o valor normal. */}
                        <HintCallout>
                            O limite <strong>não impede o cliente de agendar</strong>. Depois de usar a cota, os serviços do plano saem pelo <strong>valor normal</strong> até o próximo ciclo.
                        </HintCallout>
                    </>
                }
                size="md"
            />

            <div className="subscription-plan-form form-layout form-layout--spaced">

                {/* Plano com assinantes e sem serviços: ninguém nele recebe desconto. Fica no topo
                    porque é a primeira coisa a resolver ao abrir o plano. */}
                {Number(entityData?.openSubscriptionCount) > 0 && !(formik.values.planProducts?.length > 0) && (
                    <HintCallout icon="fa-tags" tone="warning" title={t('plan_attention.no_services_title')}>
                        {t('plan_form_no_services_warning', { count: Number(entityData.openSubscriptionCount) })}
                    </HintCallout>
                )}

                {currentProvider !== PaymentProvider.ASAAS && (
                    <div
                        className="subscription-plan-form__sync-status p-3 rounded mb-4 d-flex align-items-start border"
                        style={{
                            backgroundColor: entityData == null ? '#f8fafc' : entityData.integrationId ? '#f0fdf4' : '#fffbeb',
                            borderColor: entityData == null ? '#e2e8f0' : entityData.integrationId ? '#bbf7d0' : '#fef3c7',
                            color: entityData == null ? '#475569' : entityData.integrationId ? '#166534' : '#92400e'
                        }}
                    >
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{
                                width: '40px', height: '40px', flexShrink: 0,
                                backgroundColor: entityData == null ? '#e2e8f0' : entityData.integrationId ? '#dcfce7' : '#fef3c7',
                                color: entityData == null ? '#64748b' : entityData.integrationId ? '#22c55e' : '#f59e0b'
                            }}
                        >
                            <i className={`fas ${entityData == null ? 'fa-info' : entityData.integrationId ? 'fa-check' : 'fa-clock'} fs-5`}></i>
                        </div>
                        <div>
                            <h6 className="mb-1 fw-bold" style={{ color: 'inherit' }}>
                                {entityData == null
                                    ? t('sync_status')
                                    : t(entityData.integrationId ? 'plan_sync_synced' : 'plan_sync_pending')}
                            </h6>
                            <p className="small mb-0" style={{ opacity: 0.85 }}>
                                {entityData == null
                                    ? t('plan_sync_status_hint')
                                    : t(entityData.integrationId ? 'plan_sync_synced_hint' : 'plan_sync_pending_hint')}
                            </p>
                        </div>
                    </div>
                )}

                {/* Painel: Informações Básicas */}
                <FormSection
                    title={t('basic_information')}
                    description={t('plan_basic_info_hint')}
                    icon="fa-solid fa-credit-card"
                >

                    <Row className="mb-3">
                        <Col md={7}>
                            <FormGroup>
                                <FormLabel>{t('name')} <AsteriskRequired /></FormLabel>
                                <InputText
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.name}
                                    placeholder={t('plan_name_placeholder')}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={2}>
                            <FormGroup>
                                <FormLabel>{t('price')} <AsteriskRequired /></FormLabel>
                                <InputMoney
                                    name="price"
                                    value={formik.values.price}
                                    fixedDecimalScale
                                    onChange={(value) => formik.setFieldValue('price', value)}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.price}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <FormLabel>{t('periodicity')} <AsteriskRequired /></FormLabel>
                                <PeriodicitySelect
                                    value={formik.values.periodicity}
                                    onChange={(value) => formik.setFieldValue('periodicity', value)}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.periodicity}
                                    isDisabled={true}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={3}>
                            <FormGroup>
                                <FormLabel>
                                    Limite de usos <HintModalTrigger onClick={openMaxAppointmentsHint} label="Ver dica do limite de usos" />
                                </FormLabel>
                                <InputNumeric
                                    name="maxAppointments"
                                    value={formik.values.maxAppointments}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.maxAppointments}
                                    placeholder="Ilimitado"
                                />
                            </FormGroup>
                        </Col>
                        {/* Pacote em dias não tem ciclos de cobrança: ele vale por N dias e encerra. */}
                        {!formik.values.dailyPackage && (
                        <Col md={3}>
                            <FormGroup>
                                <FormLabel>
                                    {t('qty_transactions')} <AsteriskRequired />{' '}
                                    <HintModalTrigger onClick={openQtyTransactionsHint} label="Ver dica da quantidade de transações" />
                                </FormLabel>
                                <div className="d-flex align-items-center gap-2">
                                    <InputNumeric
                                        name="qtyTransactions"
                                        value={isIndeterminateQty ? '' : formik.values.qtyTransactions}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            setLastQtyTransactions(e.target.value);
                                        }}
                                        onBlur={formik.handleBlur}
                                        disabled={isIndeterminateQty}
                                        placeholder={isIndeterminateQty ? 'Indeterminado' : ''}
                                        style={{ width: '250px' }}
                                    />
                                    <FormCheck className="d-flex align-items-center gap-1 mb-0 flex-shrink-0">
                                        <input
                                            id="qtyTransactionsIndeterminate"
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={isIndeterminateQty}
                                            onChange={() => {
                                                if (isIndeterminateQty) {
                                                    // Desmarcou: volta pro último número de ciclos informado.
                                                    formik.setFieldValue('qtyTransactions', lastQtyTransactions || 12);
                                                } else {
                                                    // Marcou: guarda o valor atual pra restaurar se desmarcar depois.
                                                    setLastQtyTransactions(formik.values.qtyTransactions || 12);
                                                    formik.setFieldValue('qtyTransactions', 0);
                                                }
                                            }}
                                        />
                                        <FormLabel htmlFor="qtyTransactionsIndeterminate" className="form-check-label mb-0" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                            Indeterminado
                                        </FormLabel>
                                    </FormCheck>
                                </div>
                                {/* O erro fica fora da linha do campo: dentro dela virava mais um item do
                                    flex e espremia o campo e o "Indeterminado" quando a validação falhava. */}
                                <FieldValidationError error={formik.errors.qtyTransactions} />
                            </FormGroup>
                        </Col>
                        )}
                    </Row>
                    {/* Pacote na loja com duração livre. Fica separado da periodicidade porque os
                        provedores só cobram em ciclos fixos: um plano em dias nunca vai para eles. */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <div className="catalog-form__options">
                                <label className="catalog-form__option" htmlFor="dailyPackage">
                                    <div className="catalog-form__option-text">
                                        <span className="catalog-form__option-title">Pacote com duração em dias</span>
                                        <span className="catalog-form__option-hint">
                                            {packageDaysLocked
                                                ? 'Este plano já tem assinaturas em aberto, então a duração não pode ser alterada.'
                                                : 'Para pacote pago na loja, com validade de N dias (por exemplo, 45). Não é cobrado pelo provedor e encerra sozinho no fim.'}
                                        </span>
                                    </div>
                                    <FormCheck
                                        type="switch"
                                        id="dailyPackage"
                                        disabled={packageDaysLocked}
                                        checked={!!formik.values.dailyPackage}
                                        onChange={(event) => formik.setFieldValue('dailyPackage', event.target.checked)} />
                                </label>
                            </div>
                        </Col>
                        {formik.values.dailyPackage && (
                            <Col md={3} className="mt-3">
                                <FormGroup>
                                    <FormLabel>Duração (dias) <AsteriskRequired /></FormLabel>
                                    <InputNumeric
                                        name="packageDays"
                                        value={formik.values.packageDays}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={packageDaysLocked}
                                        placeholder="Ex.: 45"
                                        maxLength={3} />
                                    <FieldValidationError error={formik.errors.packageDays} />
                                </FormGroup>
                            </Col>
                        )}
                    </Row>
                    {/* Junta periodicidade e quantidade de ciclos numa frase só, para quem cadastra
                        (e confere) o plano não precisar fazer a conta de cabeça. */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <PlanCycleSummary
                                periodicity={formik.values.periodicity}
                                qtyTransactions={formik.values.qtyTransactions}
                                packageDays={formik.values.dailyPackage ? formik.values.packageDays : null} />
                        </Col>
                    </Row>
                </FormSection>

                {/* Painel: Regras de uso. Dias, desconto fora dos dias e agendamento online
                    ganharam painel próprio: dentro de "Informações básicas" eles viravam caixas
                    empilhadas sob os campos de preço e pareciam mais linhas do mesmo assunto.
                    Cada cartão responde a uma pergunta: quando o benefício vale e quantos
                    horários o assinante pode reservar. */}
                <FormSection
                    title="Regras de uso"
                    description="Em quais dias o benefício vale, quantos horários o assinante pode reservar e até quando ele vê a agenda online."
                    icon="fa-solid fa-calendar-check"
                >
                    <Row className="g-3">
                        <Col lg={7}>
                            <div className="plan-card">
                                <div className="plan-card__header">
                                    <div>
                                        <p className="plan-card__title">
                                            <i className="fa-regular fa-calendar-check me-2"></i>
                                            Dias com benefício
                                        </p>
                                        <p className="plan-card__subtitle">Dias em que os serviços do plano saem com o desconto cadastrado.</p>
                                    </div>
                                    <button type="button" className="plan-card__action" onClick={toggleAllBenefitDays}>
                                        {allBenefitDaysSelected ? 'Limpar' : 'Semana toda'}
                                    </button>
                                </div>

                                {/* Grade de 7 colunas iguais, e não botões soltos com flex-wrap:
                                    no celular os dias quebravam em duas linhas desalinhadas. */}
                                <div className="plan-card__days" role="group" aria-label="Dias com benefício">
                                    {WEEK_DAYS.map(day => (
                                        <button
                                            key={day.value}
                                            type="button"
                                            className={`plan-card__day${isBenefitDaySelected(day.value) ? ' is-selected' : ''}`}
                                            aria-pressed={isBenefitDaySelected(day.value)}
                                            onClick={() => toggleBenefitDay(day.value)}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                                {formik.errors.benefitDays && (
                                    <small className="text-danger d-block mt-2">{formik.errors.benefitDays}</small>
                                )}

                                <FormGroup className="plan-card__field">
                                    <FormLabel>
                                        Desconto nos demais dias (%){' '}
                                        <HintTooltip text="Desconto aplicado aos serviços do plano nos dias sem benefício. Vazio cobra o valor cheio. Esse atendimento nunca conta no limite de usos." />
                                    </FormLabel>
                                    <InputPercent
                                        name="offDayDiscountPercentage"
                                        value={formik.values.offDayDiscountPercentage}
                                        onChange={(value) => formik.setFieldValue('offDayDiscountPercentage', value)}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.offDayDiscountPercentage}
                                        placeholder={allBenefitDaysSelected ? 'Não se aplica' : 'Sem desconto'}
                                        disabled={allBenefitDaysSelected}
                                    />
                                </FormGroup>

                                {/* Uma frase só, dizendo o que ESTE plano faz, em vez do parágrafo
                                    genérico que descrevia as três combinações possíveis. */}
                                <p className="plan-card__summary">
                                    <i className="fa fa-circle-info"></i>
                                    <span>{benefitDaysSummary}</span>
                                </p>
                            </div>
                        </Col>

                        {/* Longe do "Limite Usos" de propósito: os dois falam de agendamento e,
                            lado a lado, pareciam a mesma coisa. Lá é quantas vezes o benefício
                            pode ser usado no ciclo; aqui é quantos horários ficam reservados ao
                            mesmo tempo no link público. */}
                        <Col lg={5}>
                            <div className="plan-card">
                                <div className="plan-card__header">
                                    <div>
                                        <p className="plan-card__title">
                                            <i className="fa-regular fa-calendar-plus me-2"></i>
                                            Agendamento online
                                        </p>
                                        <p className="plan-card__subtitle">Quantos horários o assinante pode deixar marcados e até quando vê a agenda pelo link.</p>
                                    </div>
                                </div>

                                <FormGroup className="plan-card__field">
                                    <FormLabel>
                                        Agendamentos futuros{' '}
                                        <HintTooltip text="Quantos horários o assinante pode deixar marcados ao mesmo tempo pelo link de agendamento. Não é o Limite de usos: aqui conta só o que está marcado e ainda não aconteceu. Os horários precisam caber nos dias do calendário, logo abaixo. A recepção não é limitada por este campo." />
                                    </FormLabel>
                                    <InputNumeric
                                        name="maxFutureAppointments"
                                        value={formik.values.maxFutureAppointments}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.maxFutureAppointments}
                                        placeholder={generalFutureLimit ? `Configuração geral (${generalFutureLimit})` : 'Configuração geral'}
                                    />
                                </FormGroup>

                                {/* Par dos agendamentos futuros: o plano que deixa reservar mais
                                    semanas precisa enxergar essas semanas no calendário. */}
                                <FormGroup className="plan-card__field">
                                    <FormLabel>
                                        Dias no calendário{' '}
                                        <HintTooltip text="Quantos dias o calendário do agendamento online mostra para o assinante deste plano, contando hoje. Com 30, ele vê o próximo mês enquanto o avulso fica na configuração geral. Máximo de 90. A recepção não é limitada por este campo." />
                                    </FormLabel>
                                    <InputNumeric
                                        name="publicBookingDays"
                                        value={formik.values.publicBookingDays}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.publicBookingDays}
                                        placeholder={generalBookingDays ? `Configuração geral (${generalBookingDays})` : 'Configuração geral'}
                                    />
                                </FormGroup>

                                <p className="plan-card__summary">
                                    <i className="fa fa-circle-info"></i>
                                    <span>{futureAppointmentsSummary} {bookingDaysSummary}</span>
                                </p>
                            </div>
                        </Col>
                    </Row>
                </FormSection>

                {/* Painel: Produtos/Serviços */}
                <FormSection
                    title={t('products_services')}
                    description={t('plan_products_hint')}
                    icon="fa-solid fa-gift"
                >

                    {/* Atalho: adicionar os itens de uma categoria de uma vez. Era um select de
                        categoria, um campo de % vazio e um botão Aplicar; agora cada categoria é
                        uma etiqueta clicável que já mostra quantos itens tem e quantos estão no
                        plano, e o desconto já vem em 100%. Um clique resolve o caso comum. */}
                    <div className="plan-card mb-4">
                        <div className="plan-card__header">
                            <div>
                                <p className="plan-card__title">
                                    <i className="fas fa-bolt me-2"></i>
                                    Adicionar por categoria
                                </p>
                                <p className="plan-card__subtitle">Defina o desconto e clique na categoria para incluir todos os itens dela no plano.</p>
                            </div>
                        </div>

                        <Row className="g-3">
                            <Col md={4} lg={3}>
                                <FormGroup>
                                    <FormLabel>
                                        Desconto ao adicionar (%){' '}
                                        <HintTooltip text={t('subscription_plan_discount_hint')} />
                                    </FormLabel>
                                    <InputPercent
                                        name="categoryFillPct"
                                        value={categoryFillPct}
                                        onChange={(value) => setCategoryFillPct(value)}
                                        placeholder="100,00"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={8} lg={9}>
                                <FormLabel>Categorias</FormLabel>
                                {categoryOptions.length === 0 ? (
                                    <p className="plan-card__empty">Nenhuma categoria com itens cadastrados.</p>
                                ) : (
                                    <div className="plan-card__chips" role="group" aria-label="Categorias">
                                        {categoryOptions.map((category) => {
                                            const complete = category.included === category.total;
                                            const partial = category.included > 0 && !complete;

                                            return (
                                                <button
                                                    key={category.id}
                                                    type="button"
                                                    className={`plan-card__chip${complete ? ' is-complete' : ''}`}
                                                    // Categoria toda no plano não tem o que adicionar; o clique
                                                    // não faria nada, então o botão fica desligado e diz por quê.
                                                    disabled={complete || !hasCategoryFillDiscount}
                                                    title={complete
                                                        ? 'Todos os itens desta categoria já estão no plano'
                                                        : `Adicionar ${category.total - category.included} ${category.total - category.included === 1 ? 'item' : 'itens'} com ${convertDoubleToStringPTBR(categoryFillDiscount || 0)}%`}
                                                    onClick={() => applyDiscountByCategory(category)}
                                                >
                                                    {complete && <i className="fas fa-check"></i>}
                                                    <span>{category.name}</span>
                                                    <span className="plan-card__chip-count">
                                                        {partial ? `${category.included}/${category.total}` : category.total}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </Col>
                        </Row>

                        {/* aria-live: quem usa leitor de tela ouve o resultado do clique, que não
                            muda o foco nem abre nada. */}
                        <p className="plan-card__summary mt-3" aria-live="polite">
                            <i className={`fa ${categoryFillResult ? 'fa-circle-check' : 'fa-circle-info'}`}></i>
                            <span>
                                {categoryFillResult
                                    ? (categoryFillResult.added > 0
                                        ? `${categoryFillResult.added} ${categoryFillResult.added === 1 ? 'item de' : 'itens de'} ${categoryFillResult.name} ${categoryFillResult.added === 1 ? 'adicionado' : 'adicionados'} com ${convertDoubleToStringPTBR(categoryFillResult.pct)}% de desconto. `
                                        : `Nenhum item novo em ${categoryFillResult.name}. `)
                                    : ''}
                                Itens que já estão no plano mantêm o desconto atual.
                            </span>
                        </p>
                    </div>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <FormLabel>{t('products_services')}</FormLabel>
                                <ProductMultiSelect
                                    name="products"
                                    value={formik.values.products}
                                    onChange={(value) => {
                                        formik.setFieldValue('products', value);
                                        // Sincroniza a tabela de descontos com os produtos escolhidos
                                        const newPlanProducts = value.map(v => {
                                            const existing = (formik.values.planProducts || []).find(pp => pp.productId === v.value);
                                            return existing ? existing : { productId: v.value, productName: v.label, discountPercentage: 100.0 };
                                        });
                                        formik.setFieldValue('planProducts', newPlanProducts);
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.products}
                                />

                                {formik.values.planProducts && formik.values.planProducts.length > 0 && (
                                    <div className="mt-4 pt-3 border-top">
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <h6 className="mb-0 fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                <i className="fas fa-box-open text-primary me-2"></i> Itens do Plano
                                            </h6>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                backgroundColor: 'var(--rgba-primary-1)',
                                                color: 'var(--primary)',
                                                borderRadius: '20px',
                                                padding: '3px 10px',
                                                fontSize: '0.75rem',
                                                fontWeight: '600'
                                            }}>
                                                <i className="fas fa-check-circle" style={{ fontSize: '0.7rem' }}></i>
                                                {formik.values.planProducts.length} selecionado{formik.values.planProducts.length > 1 ? 's' : ''}
                                            </span>
                                        </div>

                                        <div
                                            className="d-flex flex-column gap-2"
                                            style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '2px' }}
                                        >
                                            {formik.values.planProducts.map((pp, index) => (
                                                <div
                                                    key={pp.productId}
                                                    // bg-white: o CSS deste form (SubscriptionPlanForm.css) já
                                                    // redireciona .bg-white/.bg-light pra var(--colmeia-surface-soft),
                                                    // mas só .bg-white tem também a regra de border-color específica.
                                                    className="d-flex align-items-center justify-content-between px-3 py-2 rounded-3 bg-white"
                                                    style={{
                                                        border: `1px solid ${pp.discountPercentage === 100 ? '#bbf7d0' : '#e9ecef'}`,
                                                        minHeight: '52px'
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                            style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                backgroundColor: pp.discountPercentage === 100 ? '#dcfce7' : '#f1f5f9',
                                                                color: pp.discountPercentage === 100 ? '#16a34a' : '#94a3b8'
                                                            }}
                                                        >
                                                            <i className={`fas ${pp.discountPercentage === 100 ? 'fa-check' : 'fa-tag'}`} style={{ fontSize: '0.7rem' }}></i>
                                                        </div>
                                                        <div>
                                                            {/* var(--colmeia-ink)/--colmeia-muted: o card acima fica com
                                                                fundo escuro (var(--colmeia-surface-soft)) nessa área do
                                                                sistema, então texto com cor fixa escura ficava ilegível. */}
                                                            <span className="fw-medium" style={{ color: 'var(--colmeia-ink)', fontSize: '0.875rem' }}>{pp.productName}</span>
                                                            {(() => {
                                                                const cat = allProducts.find(p => p.id === pp.productId)?.category?.name;
                                                                return cat ? (
                                                                    <div style={{ fontSize: '0.72rem', color: 'var(--colmeia-muted)', marginTop: '1px' }}>
                                                                        {cat}
                                                                    </div>
                                                                ) : null;
                                                            })()}
                                                        </div>
                                                    </div>

                                                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                        {pp.discountPercentage === 100 && (
                                                            <span style={{
                                                                fontSize: '0.7rem', fontWeight: '600', color: '#16a34a',
                                                                backgroundColor: '#dcfce7', borderRadius: '12px', padding: '2px 8px'
                                                            }}>
                                                                Isenção Total
                                                            </span>
                                                        )}
                                                        <div className="input-group rounded-3 overflow-hidden" style={{ width: '130px' }}>
                                                            <span className="input-group-text bg-light border-end-0 text-muted px-2" style={{ fontSize: '0.8rem' }}>
                                                                <i className="fas fa-percent"></i>
                                                            </span>
                                                            <input
                                                                type="number"
                                                                className="form-control border-start-0 fw-bold text-center"
                                                                min="0"
                                                                max="100"
                                                                value={pp.discountPercentage}
                                                                onChange={(e) => {
                                                                    const val = parseFloat(e.target.value);
                                                                    const arr = [...formik.values.planProducts];
                                                                    arr[index].discountPercentage = isNaN(val) ? '' : val;
                                                                    formik.setFieldValue('planProducts', arr);
                                                                }}
                                                                style={{
                                                                    backgroundColor: pp.discountPercentage === 100 ? '#f0fdf4' : '#ffffff',
                                                                    color: pp.discountPercentage === 100 ? '#166534' : '#334155',
                                                                    height: '36px',
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-3">
                                            <HintCallout icon="fa-info-circle">
                                                Configure o desconto por serviço. Defina <strong>100%</strong> para isenção total, que abate no limite de usos do plano.
                                            </HintCallout>
                                        </div>
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

                {/* Painel: Contrato (PDF que o cliente lê e aceita ao contratar pelo site) */}
                <PlanContractSection entityData={entityData} />

                {/* Painel: Status */}
                <FormSection
                    title={t('status')}
                    description={t('plan_status_hint')}
                    icon="fa-solid fa-toggle-on"
                    compact
                >

                    <Row>
                        <Col md={12}>
                            <FormGroup className="mb-0">
                                <StatusCheckBox
                                    name="status"
                                    defaultChecked={status}
                                    checked={status}
                                    onClick={() => setStatus(!status)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSection>

            </div>
        </>
    );
}

export default Form;
